/**
 * ValueProvenance — the Valuations Wave-1 provenance chip (spec §2.2).
 *
 * Every value the app shows is a *provenanced, dated fact*: "Owner estimate ·
 * Mar 2026" is honest and fiduciary-defensible; a bare "R4.2m" is neither.
 * One shared chip renders WHAT the number is based on + AS-AT when, with the
 * staleness treatment used by the existing status pills:
 *   - fresh          → quiet dashed outline (neutral — provenance, not alarm);
 *   - stale          → amber (`--accent-100`/`--accent-700`, the "FICA due" tone);
 *   - > 12 months    → red (`--negative-soft`/`--negative`, the overdue tone).
 *
 * `basis` is optional everywhere (legacy rows never carry it) — the display
 * infers per spec §2.1 via `inferValuationBasis(source, basis)`. When a firm
 * wrote the value under an act-as session (`viaFirmActAs`), the CLIENT-facing
 * chip appends "Updated by your adviser" (§8.2 — never silent).
 *
 * Light-mode product UI: colours via CSS vars only.
 */

import {
  inferValuationBasis,
  valuationBasisLabel,
  type ValuationBasis,
  type ValuationSource,
} from '../lib/api/trusts';

export interface ValueProvenanceProps {
  /** Human provenance of the value. Absent → inferred from `source` (§2.1). */
  basis?: ValuationBasis | null;
  /** Machine channel — only used to infer a legacy row's basis. */
  source?: ValuationSource;
  /** ISO date the value is "as at". Null/absent → the chip renders the basis only. */
  asAt?: string | null;
  /** The backend per-holding staleness flag (past its revaluation due date). */
  stale?: boolean;
  /** Firm act-as provenance — renders "Updated by your adviser" (§8.2). */
  viaFirmActAs?: boolean;
  /** Replace the basis label entirely (e.g. "AFS valuation" on a trust-total
   *  chip where the basis enum doesn't apply verbatim). */
  label?: string;
  /** Compact = smaller font for dense table rows. */
  compact?: boolean;
}

/** "Mar 2026" — month + year is the honest granularity for a valuation date. */
export function formatAsAtShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
}

/** True when the as-at date is more than 12 months old. PURE. */
export function isOlderThanTwelveMonths(iso: string | null | undefined, now: Date = new Date()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - 12);
  return d.getTime() < cutoff.getTime();
}

export function ValueProvenance({
  basis,
  source,
  asAt,
  stale,
  viaFirmActAs,
  label,
  compact,
}: ValueProvenanceProps) {
  const veryOld = isOlderThanTwelveMonths(asAt);
  // Escalation: >12 months (red) beats stale (amber) beats fresh (neutral).
  const tone: 'fresh' | 'stale' | 'old' = veryOld ? 'old' : stale ? 'stale' : 'fresh';

  const toneStyle: React.CSSProperties =
    tone === 'old'
      ? { background: 'var(--negative-soft)', color: 'var(--negative)', border: '1px solid var(--negative-soft)' }
      : tone === 'stale'
        ? { background: 'var(--accent-100)', color: 'var(--accent-700)', border: '1px solid var(--accent-100)' }
        : { background: 'transparent', color: 'var(--ink-3)', border: '1px dashed var(--hairline-2)' };

  const basisText = label ?? valuationBasisLabel(inferValuationBasis(source, basis));
  const parts: string[] = [basisText];
  if (asAt) parts.push(formatAsAtShort(asAt));
  if (tone === 'old') parts.push('over 12 months old');
  else if (tone === 'stale') parts.push('due for revaluation');

  return (
    <span
      data-testid="value-provenance"
      data-tone={tone}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: compact ? 10.5 : 11.5,
        fontWeight: 500,
        lineHeight: 1.3,
        padding: compact ? '1px 7px' : '2px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        ...toneStyle,
      }}
    >
      {parts.join(' · ')}
      {viaFirmActAs ? (
        <span style={{ fontStyle: 'italic', fontWeight: 400, opacity: 0.9 }}>
          · Updated by your adviser
        </span>
      ) : null}
    </span>
  );
}
